import { Form, Head } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import BrandOgImageUploader from "@/components/brand/brand-og-image-uploader"
import LinkSharingPreviewTile from "@/components/brand/link-sharing-preview-tile"
import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import { brandSettingsLinkSharingPath } from "@/routes"

/**
 * OKS-305 — Brand Settings → Link sharing page.
 *
 * Inertia `<Form method="patch">` posting to
 * `brandSettingsLinkSharingPath()`. Form binding
 * `name="brand[og_*]"` matches the BE controller's strong-params
 * allow-list.
 *
 * Multipart-triggered automatically by the presence of
 * `<input type="file" name="brand[og_image]">` (inside
 * `<BrandOgImageUploader />`).
 *
 * Member-tier (Brand Member / Agency Member assigned) renders the page
 * in read-only mode: file input hidden, save button absent, inputs
 * `readonly`. `can_edit: boolean` from the BE policy gates.
 *
 * Live preview tile renders to the right at ≥md, stacks below at <md.
 * Pure React state, no Inertia partial reload — the admin sees the
 * effect of every keystroke + file pick immediately (UX Spec §4 / TRD
 * §K10). The `preview` Inertia prop seeds the initial values from the
 * Resolver's server-side fallback chain, so first paint matches what
 * crawlers will see.
 *
 * Soft + hard char caps (UX Spec §3 / TRD §K8):
 *   - title:       soft 60, hard 100 — Input `maxLength={100}`
 *   - description: soft 160, hard 300 — Textarea `maxLength={300}`
 */

export interface BrandSettingsLinkSharingProps {
  brand: {
    id: number
    name: string
    og_title: string | null
    og_description: string | null
    og_image_url: string | null
  }
  /** Brands::OgMetadataResolver output — seeds initial preview values
   *  from the server-side fallback chain (title may come from i18n
   *  default, image_url may come from cover_image fallback, etc.).
   *  Matches the EXACT shape the public invite landing renders. */
  preview: {
    title: string
    description: string
    image_url: string
    image_alt: string
    image_width: number
    image_height: number
    url: string
    type: string
    locale: string
    twitter_card: string
  }
  /** Server-side `BrandSettingsProfilesPolicy#update?` result (reused
   *  per TRD §K7). Brand Member / Agency Member → false. */
  can_edit: boolean
}

const OG_TITLE_SOFT_CAP = 60
const OG_TITLE_HARD_CAP = 100
const OG_DESC_SOFT_CAP = 160
const OG_DESC_HARD_CAP = 300

export default function BrandSettingsLinkSharing({
  brand,
  preview,
  can_edit,
}: BrandSettingsLinkSharingProps) {
  const { t } = useTranslation()

  // Pure-React local state. Initial values come from persisted brand
  // columns so the inputs reflect what's on file; live preview reads
  // a blend of typed-state + Resolver-fallback values.
  const [title, setTitle] = useState<string>(brand.og_title ?? "")
  const [description, setDescription] = useState<string>(
    brand.og_description ?? "",
  )
  const [pickedImageUrl, setPickedImageUrl] = useState<string | null>(null)

  // Wired through to the uploader so a fresh File swaps the preview
  // tile's image without touching server state. Revokes the previous
  // object URL to avoid leaks on rapid re-picks.
  const onFileChange = (file: File | null) => {
    if (pickedImageUrl) URL.revokeObjectURL(pickedImageUrl)
    setPickedImageUrl(file ? URL.createObjectURL(file) : null)
  }

  // Display values blend live state with Resolver-resolved fallbacks.
  // Each leg is independent — clearing the title falls back to
  // `preview.title` (Resolver's "Join {brand} on OKS Crew" default).
  const displayTitle = title.trim().length > 0 ? title : preview.title
  const displayDescription =
    description.trim().length > 0 ? description : preview.description
  const displayImageUrl = pickedImageUrl ?? preview.image_url

  // Derive the preview domain from the canonical `og:url` Resolver
  // shipped (no hardcoded host string).
  const previewDomain = (() => {
    try {
      return new URL(preview.url).host
    } catch {
      return ""
    }
  })()

  return (
    <AppLayout>
      <Head title={t("settings.brand.link_sharing.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-settings-link-sharing" className="space-y-6">
          <HeadingSmall
            title={t("settings.brand.link_sharing.title")}
            description={t("settings.brand.link_sharing.description")}
          />

          <div className="flex flex-col gap-8 md:flex-row md:items-start">
            {/* Form column */}
            <Form
              method="patch"
              action={brandSettingsLinkSharingPath()}
              options={{ preserveScroll: true }}
              className="flex-1 space-y-6"
              data-testid="brand-settings-link-sharing-form"
            >
              {({ errors, processing }) => (
                <>
                  <BrandOgImageUploader
                    currentUrl={brand.og_image_url}
                    brandName={brand.name}
                    readOnly={!can_edit}
                    onFileChange={onFileChange}
                  />
                  <InputError messages={errors.og_image} />

                  {/* Title field */}
                  <div className="grid gap-2">
                    <Label htmlFor="brand-og-title">
                      {t("settings.brand.link_sharing.og_title_label")}
                    </Label>
                    <Input
                      id="brand-og-title"
                      name="brand[og_title]"
                      maxLength={OG_TITLE_HARD_CAP}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      readOnly={!can_edit}
                      data-testid="link-sharing-title-input"
                    />
                    <CharCounter
                      current={title.length}
                      soft={OG_TITLE_SOFT_CAP}
                      hard={OG_TITLE_HARD_CAP}
                      i18nKey="settings.brand.link_sharing.og_title_counter"
                      testId="link-sharing-title-counter"
                    />
                    <p className="text-muted-foreground text-xs">
                      {t("settings.brand.link_sharing.og_title_helper")}
                    </p>
                    <InputError messages={errors.og_title} />
                  </div>

                  {/* Description field */}
                  <div className="grid gap-2">
                    <Label htmlFor="brand-og-description">
                      {t("settings.brand.link_sharing.og_description_label")}
                    </Label>
                    <Textarea
                      id="brand-og-description"
                      name="brand[og_description]"
                      rows={3}
                      maxLength={OG_DESC_HARD_CAP}
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      readOnly={!can_edit}
                      data-testid="link-sharing-description-input"
                    />
                    <CharCounter
                      current={description.length}
                      soft={OG_DESC_SOFT_CAP}
                      hard={OG_DESC_HARD_CAP}
                      i18nKey="settings.brand.link_sharing.og_description_counter"
                      testId="link-sharing-description-counter"
                    />
                    <p className="text-muted-foreground text-xs">
                      {t("settings.brand.link_sharing.og_description_helper")}
                    </p>
                    <InputError messages={errors.og_description} />
                  </div>

                  {can_edit ? (
                    <Button
                      type="submit"
                      disabled={processing}
                      data-testid="link-sharing-save"
                    >
                      {processing
                        ? t("settings.brand.link_sharing.saving")
                        : t("settings.brand.link_sharing.save")}
                    </Button>
                  ) : (
                    <p
                      role="status"
                      data-testid="brand-settings-link-sharing-readonly"
                      className="text-muted-foreground text-sm"
                    >
                      {t("settings.brand.link_sharing.read_only_hint")}
                    </p>
                  )}
                </>
              )}
            </Form>

            {/* Preview column. Sticks at scroll on ≥md per UX Spec §5. */}
            <aside
              className="w-full md:sticky md:top-6 md:w-[320px] md:shrink-0"
              data-testid="brand-settings-link-sharing-preview-aside"
            >
              <LinkSharingPreviewTile
                title={displayTitle}
                description={displayDescription}
                imageUrl={displayImageUrl}
                brandName={brand.name}
                domain={previewDomain}
              />
            </aside>
          </div>
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}

interface CharCounterProps {
  current: number
  soft: number
  hard: number
  i18nKey: string
  testId: string
}

/**
 * Small inline counter — muted under soft cap, amber at ≥90% of soft,
 * red past soft (over recommended). Suffix `(over recommended)` per
 * UX Spec §6 / A11y "colour never the sole signal".
 */
function CharCounter({
  current,
  soft,
  hard,
  i18nKey,
  testId,
}: CharCounterProps) {
  const { t } = useTranslation()
  const overSoft = current > soft
  const nearSoft = !overSoft && current >= Math.floor(soft * 0.9)
  const overHard = current > hard

  return (
    <p
      data-testid={testId}
      aria-live="polite"
      className={cn(
        "text-xs",
        !overSoft && !nearSoft && "text-muted-foreground",
        nearSoft && "text-amber-600 dark:text-amber-400",
        (overSoft || overHard) && "text-red-600 dark:text-red-400",
      )}
    >
      {t(i18nKey, { count: current })}
      {overSoft
        ? ` ${t("settings.brand.link_sharing.og_counter_over_recommended")}`
        : null}
    </p>
  )
}
